import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../../hooks/useProducts';
import { Product } from '../../types';
import SearchResultsDropdown from './SearchResultsDropdown';
import { normalizeText, expandQuery } from '../../utils/searchUtils';

interface SearchBarProps {
    className?: string;
    variant?: 'default' | 'hero';
}

const SearchBar: React.FC<SearchBarProps> = ({ className = '', variant = 'default' }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Product[]>([]);
    const [isFocused, setIsFocused] = useState(false);
    const navigate = useNavigate();
    const { products } = useProducts();
    const searchWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (query.trim() === '') {
            setResults([]);
            return;
        }

        const searchTerms = expandQuery(query);

        const filtered = products.filter(product => {
            const productText = [
                product.name,
                product.description,
                product.longDescription,
                product.category,
                product.seo?.keywords || ''
            ].join(' ');
            
            const normalizedProductText = normalizeText(productText);
            
            return searchTerms.some(term => normalizedProductText.includes(term));
        }).slice(0, 5);

        setResults(filtered);
    }, [query, products]);

    // Manejar clics fuera para cerrar el menú desplegable
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
                setIsFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (query.trim()) {
            setIsFocused(false);
            navigate(`/search?q=${encodeURIComponent(query.trim())}`);
            setQuery('');
        }
    };
    
    const showDropdown = isFocused && query.length > 0;

    const paddingClass = variant === 'hero' ? 'py-3' : 'py-2';

    return (
        <div ref={searchWrapperRef} className={`relative w-full ${className}`}>
            <form onSubmit={handleSearch}>
                <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    placeholder="Buscar tarjetas, volantes, lonas..."
                    className={`w-full pl-5 pr-12 ${paddingClass} text-gray-900 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    aria-label="Buscar productos"
                />
                <button
                    type="submit"
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-gray-500 hover:text-blue-600 rounded-full"
                    aria-label="Buscar"
                >
                    <i className="fas fa-search"></i>
                </button>
            </form>
            {showDropdown && (
                 <SearchResultsDropdown results={results} onClose={() => setIsFocused(false)} />
            )}
        </div>
    );
};

export default SearchBar;
